import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BlobServiceClient } from '@azure/storage-blob';
import { Observable, of } from 'rxjs';
import API_END_POINTS from 'src/app/core/constants/apiEndPoints';
import { AlertService } from 'src/app/core/services/alert.service';
import { sleep } from '../../utils/utility-functions';
import { FAQ } from '../../types/Faq';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private http = inject(HttpClient);
  private alertService = inject(AlertService);

  getSasUrl(type: 'raw' | 'data' | 'form') {
    return this.http.get<string>(API_END_POINTS.SAS_URL, { params: { type } });
  }

  downloadFromSas(
    fileUrl: string,
    containerName: string,
    fileName: string,
    fileNameForDownloadedFile?: string
  ) {
    const blobUrl = this.getFileBlobUrl(fileUrl, containerName, fileName);
    this.triggerDownloadByUrl(blobUrl, fileNameForDownloadedFile || fileName);
  }

  getFileBlobUrl = (
    fileUrl: string,
    containerName: string,
    fileName: string
  ) => {
    const blobServiceClient = new BlobServiceClient(fileUrl);
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(fileName);
    return blockBlobClient.url;
  };

  generatePresignedUrl = (container: string, path: string) => {
    return this.http.post<{
      url: string;
      key: string;
      expiresInSeconds: number;
    }>(API_END_POINTS.S3.GENERATE_PRESIGNED_URL, {
      key: `${container}/${path}`,
      expiresInSeconds: 3000,
    });
  };

  downloadByBlob(blob: Blob | File, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    this.triggerDownloadByUrl(url, fileName);
  }

  private triggerDownloadByUrl(url: string, fileName: string) {
    try {
      const noCacheUrl = `${url}${
        url.includes('?') ? '&' : '?'
      }_=${new Date().getTime()}`;
      const a = document.createElement('a');
      a.href = noCacheUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(noCacheUrl);
    } catch (error) {
      console.error('er', error);
    }
  }

  downloadFileFromPresignedUrl(url: string, fileName: string): void {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const blobUrl = window.URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        window.URL.revokeObjectURL(blobUrl); // Clean up
      } else {
        console.error(`Download failed with status ${xhr.status}`);
      }
    };

    xhr.onerror = () => {
      console.error('Download failed due to network error.');
    };

    xhr.send();
  }

  uploadFile(
    uploadUrl: string,
    file: File,
    folderName: string,
    fileName: string,
    chunkSize: number = 1024 * 1024 * 5,
    maxRetries: number = 10,
    desiredFileName?: string
  ): Observable<{
    uploadedChunkSize?: number;
    type?: string;
    retryAttempt?: number;
    statusCode?: number;
    remainingTimeFormatted?: number;
  }> {
    return new Observable((observer) => {
      (async () => {
        try {
          const blobServiceClient = new BlobServiceClient(uploadUrl);
          const containerClient =
            blobServiceClient.getContainerClient(folderName);
          const blockBlobClient = containerClient.getBlockBlobClient(fileName);
          const totalChunks = Math.ceil(file.size / chunkSize);
          const blockIds: string[] = [];

          let uploadedSize = 0;
          const startTime = Date.now();
          let uploadSpeeds: number[] = [];
          const chunksToAverage = 5;

          for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const chunk = file.slice(start, end);
            const randomBytes = new Uint8Array(64);
            crypto.getRandomValues(randomBytes);

            const base64String = btoa(String.fromCharCode(...randomBytes));
            const blockId = base64String;
            blockIds.push(blockId);

            let success = false;
            let attempts = 0;

            while (!success && attempts < maxRetries) {
              try {
                attempts++;
                await blockBlobClient.stageBlock(blockId, chunk, chunk.size);
                success = true;
              } catch (error) {
                if (attempts >= maxRetries) {
                  observer.error(
                    `Chunk ${i + 1} failed after ${maxRetries} attempts.`
                  );
                  this.alertService.showAlert('File Upload Failed!', 'error');
                  return;
                }
                observer.next({
                  type: 'error',
                  retryAttempt: attempts,
                  statusCode: (error as any)?.statusCode,
                });
                await sleep(1500);
              }
            }
            uploadedSize += chunk.size;
            const elapsedSeconds = (Date.now() - startTime) / 1000;
            let estimatedSeconds = 0;
            if (elapsedSeconds > 0 && uploadedSize > 0) {
              const speed = uploadedSize / elapsedSeconds;
              uploadSpeeds.push(speed);
              if (uploadSpeeds.length > chunksToAverage) {
                uploadSpeeds.shift();
              }
              const avgSpeed =
                uploadSpeeds.reduce((sum, s) => sum + s, 0) /
                uploadSpeeds.length;
              const remainingBytes = file.size - uploadedSize;

              if (avgSpeed > 0) {
                estimatedSeconds = remainingBytes / avgSpeed;
              }
            }
            observer.next({
              uploadedChunkSize: chunk.size,
              remainingTimeFormatted: Math.round(estimatedSeconds),
            });
          }
          await blockBlobClient.commitBlockList(
            blockIds,
            desiredFileName
              ? {
                  blobHTTPHeaders: {
                    blobContentDisposition: `attachment; filename="${desiredFileName}"`,
                  },
                }
              : {}
          );

          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      })();
    });
  }

  uploadToS3 = (path: string, file: File, filename?: string) => {
    const form = new FormData();
    form.append('file', file, filename ? filename : file.name); // must match backend param name
    // folderName is a query param in your Swagger definition
    return this.http.post<{
      success: boolean;
      fileName: string;
      folderName: string;
      bucketName: string;
      message: string;
    }>(
      API_END_POINTS.S3.UPLOAD_TO_S3,
      form,
      { params: { folderName: path } } // query string ?folderName=...
    );
  };

  fetchSynapseToken = () => {
    return this.http.get<{ access_token: string }>(
      API_END_POINTS.FETCH_SYNAPSE_TOKEN
    );
  };

  initiateAdfPipeline = (data: any, token: string) => {
    return of('success');
  };

  fetchFaqs = () => {
    return this.http.get<{ data: FAQ[] }>(API_END_POINTS.FAQS.FETCH_ALL_FAQ);
  };

  onReportDownload = async (
    row: { name: string; download: string },
    folderName: string
  ) => {
    this.generatePresignedUrl('data', folderName + '/' + row.name).subscribe({
      next: (res) => {
        this.downloadFileFromPresignedUrl(res.url, row.name);
      },
      error: (err) => {
        console.error(err);
      },
    });
  };
}
